import React, { useCallback, useEffect, useRef, useState } from "react"
import { APIProvider, Map, AdvancedMarker, InfoWindow, MapControl, ControlPosition, useMap } from '@vis.gl/react-google-maps'
import { useAuthDataContext } from '../../../../_providers/Auth'
import { trans } from '../../../../_providers/Translation'
import { Maps } from '../../../../_config/maps'

// images
import greenMarkerImg from '../../../../assets/img/leeflet/worker_green.png'
import orangeMarkerImg from '../../../../assets/img/leeflet/worker_orange.png'
import redMarkerImg from '../../../../assets/img/leeflet/worker_red.png'
import greenMarker2Img from '../../../../assets/img/leeflet/worker_green_bg.png'
import orangeMarker2Img from '../../../../assets/img/leeflet/worker_orange_bg.png'
import redMarker2Img from '../../../../assets/img/leeflet/worker_red_bg.png'
import messageImage from '@/assets/img/schedule/icons/message.svg'
import houseImage from '@/assets/img/schedule/icons/house.svg'

import Api from "../../../../_helpers/Api"
import { useHistory, useLocation } from "react-router-dom"
import '../../../../assets/css/modals/employees.css'
import { useMessagesContext } from "../../../../_providers/Messages"
import { useSocketContext } from "../../../../_providers/Socket"
import { FormControlLabel, Switch } from "@mui/material"
import BlueSwitch from "@/components/partials/BlueSwitch"
import GeofenceLayer from "@/components/partials/GeofenceLayer"

const defaultStyle = { width: '100%', height: '270px' }

const CENTER = { lat: 42.70060440808085, lng: 23.318481445312504 }

let listen = false

// Pick the correct marker image based on employee GPS / reservation / autoGPS flags
function getEmployeeMarkerColor(employee) {
    const { gps, currentreservation, allow_auto_gps_changes } = employee

    if (!gps) {
        return allow_auto_gps_changes ? "#fd7b7b" : "#a33434"
    }

    if (currentreservation) {
        return allow_auto_gps_changes ? "#ff8c39" : "#a05722"
    }

    return allow_auto_gps_changes ? "#73dd57" : "#3a702b"
}

// ─── Outer wrapper: provides the Google Maps API context ─────────────────────
function LocationMap({ mapId = 'location-map', mapStyle = defaultStyle, onShowAddressChange, enableGeofence = false, ...props }) {
    return (
        <APIProvider apiKey={Maps.google}>
            <LocationMapContent
                mapStyle={mapStyle}
                onShowAddressChange={onShowAddressChange}
                enableGeofence={enableGeofence}
                {...props}
            />
        </APIProvider>
    )
}

// ─── Main content: state, data loading, socket ────────────────────────────────
function LocationMapContent({ mapStyle, onShowAddressChange, enableGeofence, onHide }) {
    const auth = useAuthDataContext()
    const listener = useSocketContext()
    const history = useHistory()
    const location = useLocation()

    const [partner, setPartner] = useState(null)
    const [employees, setEmployees] = useState([])
    const [flyToBounds, setFlyToBounds] = useState(true)
    const [showAddress, setShowAddress] = useState(false)
    const [geofence, setGeofence] = useState(false)
    const [openInfoWindow, setOpenInfoWindow] = useState(null)
    const [state, setState] = useState({ refresh: false })

    useEffect(() => {
        loadPartner()
        loadEmployees()
    }, [])

    useEffect(() => {
        if (state.refresh) {
            loadEmployees()
        }
    }, [state.refresh])

    const loadCallback = () => {
        setState(prev => ({ ...prev, refresh: new Date().getTime() }))
    }

    useEffect(() => {
        if (!partner || listen) return

        listener.private(`partners.${partner.id}`)
            .listen('EmployeePositionChanged', loadCallback)

        listen = true

        return () => {
            setState(prev => ({ ...prev, refresh: false }))
            listener.private(`partners.${partner.id}`).stopListening('EmployeePositionChanged', loadCallback)
            listen = false
        }
    }, [partner])

    const loadPartner = () => {
        Api.get('partners/find?id=' + auth.user().partner_id)
            .then(res => setPartner(res.data))
    }

    const loadEmployees = () => {
        if (!auth.module('gps')) return

        Api.get('partners/employees/technicians/location')
            .then(res => {
                setEmployees(res.data)
                setFlyToBounds(false)
            })
    }

    const handleShowUpcomingReservations = useCallback(id => {
        const searchParams = new URLSearchParams(location.search)
        searchParams.set('show_upcoming_reservations', id)
        history.replace('?' + searchParams.toString())
    }, [history, location.search])

    return (
        <div style={{ position: 'relative', ...mapStyle }}>
            <Map
                defaultCenter={CENTER}
                defaultZoom={10}
                mapId="DEMO_MAP_ID"
                style={{ width: '100%', height: '100%' }}
                disableDefaultUI={true}
                keyboardShortcuts={false}
            >
                <MapOverlays
                    partner={partner}
                    employees={employees}
                    flyToBounds={flyToBounds}
                    openInfoWindow={openInfoWindow}
                    setOpenInfoWindow={setOpenInfoWindow}
                    handleShowUpcomingReservations={handleShowUpcomingReservations}
                    showAddress={showAddress}
                    onHide={onHide}
                />

                <div
                    className="legend"
                    style={{
                        background: "#fff",
                        padding: "15px",
                        position: "absolute",
                        right: "15px",
                        bottom: "15px",
                        width: "fit-content",
                        boxShadow: '0 18px 40px 0 rgba(112, 144, 176, 0.19)',
                        maxWidth: "300px",
                        borderRadius: "16px"
                    }}
                >
                    {[
                        { name: trans('random.free'), cls: 'free' },
                        { name: trans('random.busy'), cls: 'busy' },
                        { name: trans('random.gpsoff'), cls: 'gps' },
                        { name: trans('random.autogps'), cls: 'autogps' },
                        { name: trans('random.manualgps'), cls: 'manual' },
                    ].map(s => (
                        <p key={s.cls}><span className={s.cls}></span>{s.name}</p>
                    ))}
                </div>


                <FormControlLabel
                    control={
                        <BlueSwitch
                            checked={showAddress}
                            onChange={e => {
                                const v = e.target.checked
                                setShowAddress(v)
                                if (typeof onShowAddressChange === 'function') onShowAddressChange(v)
                            }}
                        />
                    }
                    className="location-map-switch"
                    label={trans('random.showAddressOnMap')}
                />

                {enableGeofence && (
                    <>
                        <FormControlLabel
                            control={
                                <BlueSwitch
                                    checked={geofence}
                                    onChange={e => setGeofence(e.target.checked)}
                                />
                            }
                            className="location-map-switch geofence-switch"
                            label={trans('random.geofences')}
                        />

                        <GeofenceLayer
                            active={geofence}
                            useOsmBoundaries
                        />
                    </>
                )}
            </Map>
        </div>
    )
}

// ─── Home pin SVG icon ────────────────────────────────────────────────────────
function HomePinIcon({ name }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{
                width: 32,
                height: 32,
                background: '#fff',
                border: '2px solid #4a90d9',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                fontSize: 16,
            }}>
                <img src={houseImage} alt="home" style={{ width: 16, height: 16 }} />
            </div>
            {name && (
                <span style={{
                    marginTop: 3,
                    background: '#fff',
                    border: '1px solid #4a90d9',
                    borderRadius: 6,
                    padding: '1px 6px',
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#4a90d9',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                }}>
                    {name}
                </span>
            )}
        </div>
    )
}

// ─── Directions route from employee home to current position ──────────────────
function EmployeeHomeRoute({ employee }) {
    const map = useMap()
    const rendererRef = useRef(null)

    useEffect(() => {
        if (!map) return

        const homePos = { lat: parseFloat(employee.home_lat), lng: parseFloat(employee.home_lon) }
        const currentPos = { lat: parseFloat(employee.lat), lng: parseFloat(employee.lon) }

        const renderer = new window.google.maps.DirectionsRenderer({
            map,
            suppressMarkers: true,
            preserveViewport: true,
            polylineOptions: {
                strokeColor: getEmployeeMarkerColor(employee),
                strokeOpacity: 0.8,
                strokeWeight: 4,
            },
        })
        rendererRef.current = renderer

        const service = new window.google.maps.DirectionsService()
        service.route(
            { origin: homePos, destination: currentPos, travelMode: window.google.maps.TravelMode.DRIVING },
            (result, status) => {
                if (status === 'OK') renderer.setDirections(result)
            }
        )

        return () => {
            if (rendererRef.current) {
                rendererRef.current.setMap(null)
                rendererRef.current = null
            }
        }
    }, [map, employee.home_lat, employee.home_lon, employee.lat, employee.lon])

    return null
}

// ─── Map overlays (must live inside <Map> context) ────────────────────────────
function MapOverlays({ partner, employees, flyToBounds, openInfoWindow, setOpenInfoWindow, handleShowUpcomingReservations, showAddress, onHide }) {
    const map = useMap()
    const messages = useMessagesContext()
    const partnerCircleRef = useRef(null)

    // Render the partner office as a red circle on the map
    useEffect(() => {
        if (!map || !partner?.lat || !partner?.lon) return

        if (partnerCircleRef.current) {
            partnerCircleRef.current.setMap(null)
        }

        const circle = new window.google.maps.Circle({
            map,
            center: { lat: parseFloat(partner.lat), lng: parseFloat(partner.lon) },
            radius: 50,
            strokeColor: '#f03',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: '#f03',
            fillOpacity: 0.5,
        })

        circle.addListener('click', () => setOpenInfoWindow('partner'))

        partnerCircleRef.current = circle

        return () => {
            if (partnerCircleRef.current) {
                partnerCircleRef.current.setMap(null)
            }
        }
    }, [map, partner])

    // Fit viewport to all employee markers on first load only
    useEffect(() => {
        if (!map || !flyToBounds) return

        const withCoords = employees.filter(e => e.lat && e.lon)
        if (!withCoords.length) return

        const bounds = new window.google.maps.LatLngBounds()
        withCoords.forEach(e => bounds.extend({ lat: parseFloat(e.lat), lng: parseFloat(e.lon) }))
        map.fitBounds(bounds)
    }, [map, employees, flyToBounds])

    // Trigger resize when the map container may have changed size
    useEffect(() => {
        if (!map) return
        window.google.maps.event.trigger(map, 'resize')
    }, [map])

    return (
        <>
            {/* Partner circle InfoWindow */}
            {partner?.lat && partner?.lon && openInfoWindow === 'partner' && (
                <InfoWindow
                    position={{ lat: parseFloat(partner.lat), lng: parseFloat(partner.lon) }}
                    onCloseClick={() => setOpenInfoWindow(null)}
                >
                    <div>{partner.name}</div>
                </InfoWindow>
            )}

            {/* Home pins + routes (visible only when showAddress is on) */}
            {showAddress && employees
                .filter(e => e.lat && e.lon && e.home_lat && e.home_lon)
                .map(e => {
                    const homeKey = `home-${e.id}`
                    const homePos = { lat: parseFloat(e.home_lat), lng: parseFloat(e.home_lon) }
                    return (
                        <React.Fragment key={homeKey}>
                            <AdvancedMarker
                                position={homePos}
                            >
                                <HomePinIcon name={`${e.name} ${e.lastname}`} />
                            </AdvancedMarker>
                            {openInfoWindow === homeKey && (
                                <InfoWindow
                                    position={homePos}
                                    onCloseClick={() => setOpenInfoWindow(null)}
                                    pixelOffset={[0, -60]}
                                >
                                    <div style={{ fontWeight: 600 }}>{e.name} {e.lastname}</div>
                                </InfoWindow>
                            )}
                            <EmployeeHomeRoute employee={e} />
                        </React.Fragment>
                    )
                })
            }

            {/* Employee markers */}
            {employees.filter(e => e.lat && e.lon).map(e => {
                const key = `emp-${e.id}`
                const pos = { lat: parseFloat(e.lat), lng: parseFloat(e.lon) }

                return (
                    <React.Fragment key={key}>
                        <AdvancedMarker
                            position={pos}
                            onClick={() => setOpenInfoWindow(openInfoWindow === key ? null : key)}
                        >
                            <div style={{
                                width: 44,
                                height: 44,
                                borderRadius: '50%',
                                overflow: 'hidden',
                                border: `3px solid ${getEmployeeMarkerColor(e)}`,
                                boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
                                backgroundColor: '#ffe6cc',
                                flexShrink: 0,
                            }}>
                                <img
                                    src={e.avatar}
                                    alt="employee"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                />
                            </div>
                        </AdvancedMarker>

                        {openInfoWindow === key && (
                            <InfoWindow
                                position={pos}
                                onCloseClick={() => setOpenInfoWindow(null)}
                                pixelOffset={[0, -60]}
                                headerContent={<>
                                    <div
                                        className="head"
                                        style={{
                                            flexDirection: "column",
                                            alignItems: 'flex-start',
                                            marginBottom: "5px"
                                        }}
                                    >
                                        <span className={`label ${e.currentreservation ? 'busy' : 'free'}`}>
                                            {e.currentreservation ? trans('random.busy') : trans('random.free')}
                                        </span>
                                        <span
                                            className="name"
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "5px",
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => {
                                                messages.openChat({
                                                    id: e.id,
                                                    type: e.type,
                                                    name: e.name,
                                                    lastname: e.lastname,
                                                    avatar: e.avatar,
                                                    tenant_id: e.tenant_id,
                                                    receiver_tenant_id: e.tenant_id // ensure receiver_tenant_id is set
                                                })
                                                onHide()
                                            }}>
                                            {e.name} {e.lastname}
                                            <img src={messageImage} alt="message" style={{ width: 25, height: 25 }} />
                                        </span>
                                    </div>
                                </>}
                            >
                                <div className="custom">


                                    {e.currentreservation && (
                                        <div className="body">
                                            <p>{e.currentreservation.full_address}</p>
                                        </div>
                                    )}

                                    <span
                                        className="show-upcoming"
                                        onClick={() => handleShowUpcomingReservations(e.id)}
                                        style={{
                                            cursor: 'pointer',
                                            marginTop: "7px",
                                            display: "block",
                                            textDecoration: "underline",
                                            fontSize: "12px"
                                        }}
                                    >
                                        {trans('buttons.viewReservations')}
                                    </span>
                                </div>
                            </InfoWindow>
                        )
                        }
                    </React.Fragment >
                )
            })}
        </>
    )
}

export default LocationMap
